<template>
    <div class="mc-tech-popup" data-testid="technician-popup">
        <!-- Header -->
        <header class="mc-tech-popup__header" :class="statusClass">
            <div class="mc-tech-popup__title-row">
                <div>
                    <h3 class="mc-tech-popup__name">{{ detail.name }}</h3>
                    <div class="mc-tech-popup__status">
                        <span class="status-dot" :class="dotClass"></span>
                        <span>{{ statusLabel }}</span>
                    </div>
                </div>
            </div>

            <div v-if="detail.status === 'B' && detail.clock_in_photo" class="mc-tech-popup__clockin-wrapper">
                <button type="button" class="mc-tech-popup__clockin" data-testid="popup-clockin-btn"
                    @click="$emit('show-clock-in', detail)">
                    <i class="mc-fa icon-fill_camera" aria-hidden="true"></i>
                    Foto Clock-In
                </button>
            </div>
        </header>

        <!-- Body -->
        <div class="mc-tech-popup__body">
            <dl class="mc-tech-popup__details">
                <div class="mc-tech-popup__detail-row">
                    <dt>{{ $t('liveView.infoWindow.lastSeen') }}</dt>
                    <dd>{{ detail.logged_at ? `${momentDMYH(detail.logged_at)} WIB` : '-' }}</dd>
                </div>
                <div class="mc-tech-popup__detail-row">
                    <dt>{{ $t('location.wo') }}</dt>
                    <dd>{{ detail.location || '-' }}</dd>
                </div>
                <div class="mc-tech-popup__detail-row">
                    <dt>{{ $t('workOrder.table.woNumber') }}</dt>
                    <dd>{{ detail.wo_number || '-' }}</dd>
                </div>
                <div class="mc-tech-popup__detail-row">
                    <dt>{{ $t('workOrder.table.customer') }}</dt>
                    <dd>{{ detail.customer || '-' }}</dd>
                </div>
                <div class="mc-tech-popup__detail-row">
                    <dt>{{ $t('workOrder.table.actualInstall') }}</dt>
                    <dd>{{ detail.actual_installation ?? 0 }}</dd>
                </div>
                <div v-if="technicianHomeAddress" class="mc-tech-popup__detail-row">
                    <dt>Lokasi Teknisi</dt>
                    <dd>{{ technicianHomeAddress }}</dd>
                </div>
            </dl>

            <div v-if="detail.workOrders && detail.workOrders.length" class="mc-tech-popup__wo-table">
                <small class="text-muted fw-semibold d-block mb-1">Outstanding Work Orders</small>
                <div class="table-responsive">
                    <b-table class="datatables mb-0" :items="detail.workOrders" :fields="visibleFields" responsive="sm"
                        show-empty :empty-text="$t('global.emptyTable')" small striped hover>
                        <template #cell(wo_number)="data">
                            <a :href="generateLink(data.item)" class="text-primary fw-semibold">
                                {{ data.item.wo_number }}
                            </a>
                        </template>
                        <template #cell(plan_date)="data">
                            {{ momentDMHmsUtc(data.item.plan_date) }}
                        </template>
                    </b-table>
                </div>
            </div>
        </div>

        <!-- Footer actions -->
        <footer class="mc-tech-popup__footer">
            <button type="button" class="mc-tech-popup__action" data-testid="popup-open-gmap-btn"
                @click="$emit('open-gmap')">
                <i class="fas fa-map-marked-alt" aria-hidden="true"></i>
                Google Maps
            </button>
            <button type="button" class="mc-tech-popup__action" data-testid="popup-share-wa-btn"
                @click="$emit('share', 'wa')">
                <i class="fab fa-whatsapp" aria-hidden="true"></i>
                WhatsApp
            </button>
            <button type="button" class="mc-tech-popup__action" data-testid="popup-share-copy-btn"
                @click="$emit('share', 'copy')">
                <i class="fas fa-copy" aria-hidden="true"></i>
                Salin
            </button>
        </footer>
    </div>
</template>

<script>
import { Helper } from '@/mixin/helper'

export default {
    name: 'TechnicianPopup',
    mixins: [Helper],
    props: {
        detail: { type: Object, required: true },
        visibleFields: { type: Array, default: () => [] },
        generateLink: { type: Function, required: true },
    },
    computed: {
        technicianHomeAddress() {
            const addr = this.detail.address;
            if (!addr) return 'Lokasi rumah tidak diketahui';
            // Hanya tampilkan jika ada teks alamat yang bermakna
            if (addr.address) return addr.address;
            if (addr.location) return addr.location;
            // Jangan tampilkan koordinat atau pesan fallback
            return 'Lokasi rumah tidak diketahui';
        },
        statusLabel() {
            if (this.detail.status === 'B') return this.$t('location.sideBar.status.busy')
            if (this.detail.status === 'A') return this.$t('location.sideBar.status.available')
            return this.$t('location.sideBar.status.notInstalledYet')
        },
        statusClass() {
            if (this.detail.status === 'B') return 'is-busy'
            if (this.detail.status === 'A') return 'is-available'
            return 'is-niy'
        },
        dotClass() {
            if (this.detail.status === 'B') return 'status-dot--busy'
            if (this.detail.status === 'A') return 'status-dot--available'
            return 'status-dot--niy'
        },
    },
}
</script>

<style scoped>
.mc-tech-popup {
    font-family: inherit;
    width: 320px;
    max-width: 100%;
    background: #ffffff;

    display: flex;
    flex-direction: column;

    /* Ganti height: 100vh menjadi max-height + height 100% */
    height: 100%;
    /* mengisi parent */
    max-height: 100vh;
    /* jaga agar tidak melebihi viewport */
    overflow: hidden;
    /* pastikan tidak ada konten yang bocor */
}

/* Header */
.mc-tech-popup__header {
    padding: 14px 16px 12px;
    border-bottom: 1px solid #eef2f7;
    background: #f8fafc;
    position: relative;
    min-height: 110px;
    /* Sesuaikan nilai ini dengan desain yang diinginkan */
}

.mc-tech-popup__header.is-busy {
    background: #fef2f2;
}

.mc-tech-popup__header.is-available {
    background: #f0fdf4;
}

.mc-tech-popup__header.is-niy {
    background: #fffbeb;
}

.mc-tech-popup__title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.mc-tech-popup__name {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
}

.mc-tech-popup__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
}

.mc-tech-popup__close {
    background: transparent;
    border: none;
    padding: 4px;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
    line-height: 1;
    transition: background 0.15s ease;
}

.mc-tech-popup__close:hover {
    background: rgba(15, 23, 42, 0.08);
    color: #0f172a;
}

.mc-tech-popup__clockin-wrapper {
    margin-top: 8px;
    height: 32px;
    /* sesuaikan dengan tinggi tombol + padding */
    /* opsional: display: flex; align-items: center; jika ingin tombol di tengah */
}

.mc-tech-popup__clockin {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #ffffff;
    border: 1px solid #e5e9f0;
    color: #b91c1c;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
}

.mc-tech-popup__clockin:hover {
    background: #fee2e2;
}

/* Body */
.mc-tech-popup__body {
    padding: 12px 16px;
    flex: 1;
    /* mengambil sisa ruang */
    overflow-y: auto;
    /* scroll internal jika konten panjang */
}

.mc-tech-popup__details {
    margin: 0;
    display: grid;
    gap: 8px;
}

.mc-tech-popup__detail-row {
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 8px;
    font-size: 0.8125rem;
    align-items: start;
}

.mc-tech-popup__detail-row dt {
    color: #64748b;
    font-weight: 500;
    margin: 0;
}

.mc-tech-popup__detail-row dd {
    color: #0f172a;
    margin: 0;
    word-break: break-word;
}

/* WO Table */
.mc-tech-popup__wo-table {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #e5e9f0;
}

.mc-tech-popup__wo-table ::v-deep table {
    font-size: 0.75rem;
}

/* Footer */
.mc-tech-popup__footer {
    display: flex;
    gap: 6px;
    padding: 10px 12px;
    background: #f8fafc;
    border-top: 1px solid #eef2f7;
}

.mc-tech-popup__action {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 6px 8px;
    background: #ffffff;
    border: 1px solid #e5e9f0;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    transition: all 0.15s ease;
}

.mc-tech-popup__action:hover {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
}

/* Status dot */
.status-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #94a3b8;
}

.status-dot--busy {
    background: #dc2626;
}

.status-dot--available {
    background: #16a34a;
}

.status-dot--niy {
    background: #d97706;
}
</style>
